import React, { FC, Fragment, ReactElement, useCallback, useContext } from 'react'
import get from 'get-value'
import {
    BackgroundMode,
    BrandingData,
    BrandingElement,
    BrandingFieldTypes,
    CMEBranding,
    ColourBranding,
    FileBranding,
    LightDark,
    LinkBranding,
    TextBranding,
    UpdateTo,
    Validations
} from '../Types'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import { getColourFieldName, getImageFieldName, getLanguagesFieldName } from '../FieldName'
import FormRow from '../../Common/Components/FormRow'
import { ColourPicker } from '../../../ReactRouting/Components/Atoms/ColourPicker/ColourPicker'
import { EMPTY_STRING, SPACE } from '../../Common/Constants/Separators'
import { BrandingLanguage } from '../../../../i18n'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { FileUpload } from '../../Common/Components/FileUpload/FileUpload'
// eslint-disable-next-line import/no-cycle
import { BrandingExpandableList } from './BrandingExpandableList'
import { Rules } from '../../Common/Types/Types'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { Tags } from '../../Common/API/Contentful/Constants'
import { IsStringEmpty, isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { LinkInput } from '../../Common/Components/LinkInput/LinkInput'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { useBrandingFeatureFlag } from './BrandingFeatureFlagContext'
import { BrandingFeatureFlagPredicate } from './BrandingFeatureFlagUtils'

type Props = {
    brandingData: BrandingData
    viewOnly: boolean
    fieldNameFunction: (fieldName: string) => string
    selectedCME?: string
    rules?: Rules
    existingBranding?: CMEBranding
}

export const BrandingFormEntry: FC<Props> = ({
    brandingData,
    viewOnly,
    fieldNameFunction,
    selectedCME,
    rules,
    existingBranding
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { watch }: UseFormMethods = useFormContext()

    const isFeatureEnabled: BrandingFeatureFlagPredicate = useBrandingFeatureFlag()

    const { rootOnly, validations, featureFlag, hint }: BrandingElement = brandingData
    const { required }: Validations = validations ?? {}

    const requiredRule: {
        required?: string
    } = required ? { required: language.get(BrandingLanguage.Branding_Required_Message) } : {}

    const withHelp = (formEntry: ReactElement, helpID?: string): ReactElement => (
        <div className='branding-form-entry'>
            <div className='bfe-content'>
                {formEntry}
                {isStringNotEmpty(hint) ? <div className='bfe-hint'>{language.get(hint) ?? hint}</div> : <></>}
            </div>
            {isStringNotEmpty(helpID) ? <HelpTag helpContentTitle={helpID} tag={Tags.BRANDING} /> : <></>}
        </div>
    )

    if (isFeatureEnabled(featureFlag) && (!rootOnly || (rootOnly && selectedCME === DEFAULT_CME_ID))) {
        if (brandingData.type === BrandingFieldTypes.COLOUR) {
            const { fieldName, label, defaultValue, hasDarkMode, updateTo, helpID }: ColourBranding = brandingData

            const brandingFieldName: Definable<LightDark> = getColourFieldName(
                fieldNameFunction?.(fieldName) ?? fieldName
            )
            const updateToProcessed: Nullable<UpdateTo[]> = updateTo?.map(
                (update: UpdateTo): UpdateTo => ({
                    ...update,
                    name: getColourFieldName(update.name)?.light ?? update.name
                })
            )

            const darkModeLabel: (languageID: string) => string = useCallback(
                (languageID: string): string =>
                    `${language.get(languageID)} ${language.get(BrandingLanguage.Branding_Labels_DarkMode)}`,
                [language]
            )

            return withHelp(
                <>
                    <FormRow>
                        <ColourPicker
                            label={language.get(label) ?? label}
                            name={brandingFieldName?.light ?? EMPTY_STRING}
                            defaultValue={defaultValue}
                            rules={{ ...rules, ...requiredRule }}
                            updateTo={[
                                ...(updateToProcessed ?? []),
                                ...(hasDarkMode
                                    ? [
                                          {
                                              name: brandingFieldName?.dark ?? EMPTY_STRING
                                          }
                                      ]
                                    : [])
                            ].filter((update: UpdateTo): boolean => {
                                const valueOfExistingBrandingEntry: Nullable<string> = get(
                                    existingBranding as object,
                                    update.name.replace('branding.', EMPTY_STRING)
                                ) as Nullable<string>

                                return IsStringEmpty(valueOfExistingBrandingEntry)
                            })}
                            disabled={viewOnly}
                        />
                    </FormRow>
                    {hasDarkMode ? (
                        <FormRow>
                            <ColourPicker
                                label={
                                    language.get(label)
                                        ? darkModeLabel(label)
                                        : `${label} ${language.get(BrandingLanguage.Branding_Labels_DarkMode)}`
                                }
                                rules={{ ...rules, ...requiredRule }}
                                name={brandingFieldName?.dark ?? EMPTY_STRING}
                                defaultValue={defaultValue}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                </>,
                helpID
            )
        }

        if (brandingData.type === BrandingFieldTypes.FILE) {
            const { fieldName, label, type, helpID, fileTypeFieldName, bgMode }: FileBranding = brandingData
            const { maxFileSize, dimensions, accepts }: Validations = validations ?? {}

            const imageFieldName: string = getImageFieldName(fieldNameFunction?.(fieldName) ?? fieldName)
            const typeFieldName: Definable<string> = isStringNotEmpty(fileTypeFieldName)
                ? getImageFieldName(fieldNameFunction?.(fileTypeFieldName) ?? fileTypeFieldName)
                : undefined

            switch (type) {
                case BrandingFieldTypes.FILE:
                    return withHelp(
                        <ValidationErrors key={fieldName} name={imageFieldName}>
                            <FileUpload
                                name={imageFieldName}
                                typeFieldName={typeFieldName}
                                label={language.get(label) ?? label}
                                maxFileSize={maxFileSize}
                                dimensions={dimensions}
                                allowedTypes={accepts}
                                disabled={viewOnly}
                                rules={{ ...rules, ...requiredRule }}
                                bgMode={bgMode}
                                primaryColour={watch('branding.colours.primary-color') as BackgroundMode}
                            />
                        </ValidationErrors>,
                        helpID
                    )
                default:
                    return <Fragment key={fieldName}></Fragment>
            }
        }

        if (brandingData.type === BrandingFieldTypes.TEXT) {
            const { fieldName, label, helpID }: TextBranding = brandingData
            const { maxLength, maxWordLength, pattern }: Validations = validations ?? {}

            const brandingFieldName: string = getLanguagesFieldName(fieldNameFunction?.(fieldName) ?? fieldName)

            return withHelp(
                <FormRow key={brandingFieldName}>
                    <ValidationErrors name={brandingFieldName}>
                        <OutlinedTextInput
                            inputName={brandingFieldName}
                            labelLeft={language.get(label) ?? label}
                            disabled={viewOnly}
                            maxLength={maxLength}
                            rules={{
                                ...rules,
                                ...requiredRule,
                                validate: {
                                    ...rules?.validate,
                                    ...(maxWordLength
                                        ? {
                                              maxWordLength: (value: string): ValidateResult =>
                                                  value
                                                      .split(SPACE)
                                                      .every((word: string): boolean => word.length < maxWordLength)
                                                      ? undefined
                                                      : language.getInterpolated(
                                                            BrandingLanguage.Branding_Validation_MaxWordLength,
                                                            maxWordLength
                                                        )
                                          }
                                        : {}),
                                    ...(maxLength
                                        ? {
                                              maxLength: (value: string): ValidateResult =>
                                                  value.length <= maxLength
                                                      ? undefined
                                                      : language.getInterpolated(
                                                            BrandingLanguage.Branding_Validation_MaxLength,
                                                            maxLength
                                                        )
                                          }
                                        : {}),
                                    pattern: (value: string): ValidateResult => {
                                        if (pattern) {
                                            return new RegExp(pattern, 'ig').test(value)
                                                ? undefined
                                                : language.get(BrandingLanguage.Branding_Validation_MustBeValidURL)
                                        }

                                        return undefined
                                    }
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>,
                helpID
            )
        }

        if (brandingData.type === BrandingFieldTypes.LIST) {
            return withHelp(
                <BrandingExpandableList
                    parentFieldNameFunction={fieldNameFunction}
                    listBranding={brandingData}
                    viewOnly={viewOnly}
                />,
                brandingData.helpID
            )
        }

        if (brandingData.type === BrandingFieldTypes.LINK) {
            const { fieldName, label, helpID }: LinkBranding = brandingData
            const { pattern, maxLength, maxWordLength }: Validations = validations ?? {}

            const brandingFieldName: string = getLanguagesFieldName(fieldNameFunction?.(fieldName) ?? fieldName)

            return withHelp(
                <LinkInput
                    name={brandingFieldName}
                    label={isStringNotEmpty(language.get(label)) ? language.get(label) : label}
                    disabled={viewOnly}
                    rules={{
                        ...rules,
                        ...requiredRule,
                        validate: {
                            ...rules?.validate,
                            pattern: (value: string): ValidateResult => {
                                if (pattern) {
                                    return new RegExp(pattern, 'ig').test(value)
                                        ? undefined
                                        : language.get(BrandingLanguage.Branding_Validation_MustBeValidURL)
                                }

                                return undefined
                            }
                        }
                    }}
                    textRules={{
                        ...requiredRule,
                        validate: {
                            ...(maxLength
                                ? {
                                      maxLength: (value: string): ValidateResult =>
                                          value.length <= maxLength
                                              ? undefined
                                              : language.getInterpolated(
                                                    BrandingLanguage.Branding_Validation_MaxLength,
                                                    maxLength
                                                )
                                  }
                                : {}),
                            ...(maxWordLength
                                ? {
                                      maxWordLength: (value: string): ValidateResult =>
                                          value
                                              .split(SPACE)
                                              .every((word: string): boolean => word.length < maxWordLength)
                                              ? undefined
                                              : language.getInterpolated(
                                                    BrandingLanguage.Branding_Validation_MaxWordLength,
                                                    maxWordLength
                                                )
                                  }
                                : {})
                        }
                    }}
                />,
                helpID
            )
        }
    }

    return <Fragment key={brandingData.fieldName}></Fragment>
}
